import { SearchTeam, SearchUserEnriched } from 'lib-search-types';
import { User } from 'lib-shared-types-api';
import { useEffect, useMemo, useState } from 'react';

import { AwayStatus } from 'apis/presence';
import { FavoritesPresenceListSection } from 'components/presence/components/favoritesPresenceListSection';
import { PresenceRowSelection } from 'components/presence/components/presenceListItem';
import { StatusPresenceListSections } from 'components/presence/components/statusPresenceListSections';
import { TeamPresenceListSections } from 'components/presence/components/teamPresenceListSections';
import { useCollapsedSections } from 'components/presence/hooks/useCollapsedSections';
import { sortMembers } from 'components/presence/utils';
import { useFavoriteAgents } from 'utils/useFavoriteAgents';

export type TeamWithMembers = {
  id: string;
  name: string;
  members: SearchUserEnriched[];
};

type PresenceListSectionsProps = {
  profile: User;
  awayStatuses: AwayStatus[];
  members: SearchUserEnriched[];
  teams: readonly SearchTeam[];
  isLoading: boolean;
  isError: boolean;
  /** Whether an agent's popover is open, which also freezes the favorites. */
  isPopoverOpen: boolean;
  selection: PresenceRowSelection;
};

export const PresenceListSections = ({
  profile,
  awayStatuses,
  members,
  teams,
  isLoading,
  isError,
  isPopoverOpen,
  selection,
}: PresenceListSectionsProps): JSX.Element => {
  const { favoriteAgentIds } = useFavoriteAgents();
  const collapsedSections = useCollapsedSections();

  // Frozen favorites state - prevents list jumping while the dropdown or an
  // agent's popover is open, where favorites can be added and removed
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const isFrozen = isDropdownOpen || isPopoverOpen;

  const [frozenFavoriteIds, setFrozenFavoriteIds] =
    useState<readonly string[]>(favoriteAgentIds);

  useEffect(() => {
    if (!isFrozen) {
      setFrozenFavoriteIds(favoriteAgentIds);
    }
  }, [favoriteAgentIds, isFrozen]);

  const favoriteMembers = useMemo(() => {
    const favorites = members.filter(
      (member) =>
        member.id === profile.id || frozenFavoriteIds.includes(member.id),
    );
    const sorted = sortMembers(favorites);
    const ownMember = sorted.find((member) => member.id === profile.id);
    const others = sorted.filter((member) => member.id !== profile.id);
    return ownMember ? [ownMember, ...others] : others;
  }, [members, profile.id, frozenFavoriteIds]);

  const membersById = useMemo(
    () => new Map(members.map((member) => [member.id, member] as const)),
    [members],
  );

  const teamsWithMembers: TeamWithMembers[] = useMemo(
    () =>
      teams
        .map((team) => ({
          id: team.id,
          name: team.name,
          members: sortMembers(
            (team.agents ?? [])
              .map((agentId) => membersById.get(agentId))
              .filter((member): member is SearchUserEnriched => !!member),
          ),
        }))
        .filter((team) => team.members.length > 0),
    [teams, membersById],
  );

  return (
    <>
      <FavoritesPresenceListSection
        profile={profile}
        awayStatuses={awayStatuses}
        members={favoriteMembers}
        loading={isLoading}
        selection={selection}
        onDropdownOpenChange={setIsDropdownOpen}
        collapsedSections={collapsedSections}
      />
      <TeamPresenceListSections
        profile={profile}
        awayStatuses={awayStatuses}
        teams={teamsWithMembers}
        loading={isLoading}
        error={isError}
        selection={selection}
        collapsedSections={collapsedSections}
      />
      {!isLoading && (
        <StatusPresenceListSections
          members={members}
          profile={profile}
          awayStatuses={awayStatuses}
          selection={selection}
          collapsedSections={collapsedSections}
        />
      )}
    </>
  );
};
